import { Copy } from 'lucide-react'

import { Button, Typography } from '~/components'
import { Modal, ModalBody, ModalFooter, ModalHeaderContent } from '~/components/modals/CurieModal'
import { BetterToast } from '~/utils/toast'

const SCHEDULE_EXAMPLE_PROMPT =
  'Create a scheduled task which scans the code for any security vulnerabilities every week by analyzing the connectors, code, and searching the web, then opens a PR with findings and fixes.'

export function ScheduledTaskInstructionsModal({
  isOpen,
  onClose
}: {
  isOpen: boolean
  onClose: () => void
}) {
  return (
    <Modal isOpen={isOpen} onClose={onClose} className='max-w-[640px] w-full'>
      <ModalBody className='flex flex-col gap-4'>
        <ModalHeaderContent
          title='Scheduling instructions'
          description='How to create scheduled agent runs from chat.'
          className='items-start text-left'
        />
        <ScheduledTasksHintCard />
      </ModalBody>
      <ModalFooter>
        <Button variant='outlined' onClick={onClose}>
          Close
        </Button>
      </ModalFooter>
    </Modal>
  )
}

/**
 * Always-visible hint explaining what the chat assistant can schedule today.
 * Kept in sync with the LLM tool's constraints in
 * `agents/src/agents/assistants/mule/landing_page_agent/agent_task_as_tools.py`
 * (code PR and integration-test schedules, ONCE / INTERVAL only).
 */
export function ScheduledTasksHintCard() {
  return (
    <section
      className='w-full rounded-[12px] border border-tokens-neutral-border-1 bg-tokens-neutral-fill-fill-6 px-5 py-4 flex flex-col gap-3'
      data-testid='scheduled-tasks-hint'
      aria-label='What you can schedule from chat'
    >
      <Typography
        fontFamily='geist'
        fontSize='bodyMdMedium'
        fontWeight='semibold'
        className='select-none'
      >
        What you can schedule from chat today
      </Typography>

      <ul className='flex flex-col gap-1.5 list-disc pl-5'>
        <li>
          <Typography
            fontFamily='geist'
            fontSize='bodyMd'
            className='select-none text-tokens-neutral-text-secondary'
          >
            <strong>Supported scheduled runs</strong> — chat can schedule code PR tasks and
            integration test tasks. Settings currently creates code PR schedules.
          </Typography>
        </li>
        <li>
          <Typography
            fontFamily='geist'
            fontSize='bodyMd'
            className='select-none text-tokens-neutral-text-secondary'
          >
            <strong>Code PR schedules need one git repository</strong> — open a chat with a single
            GitHub, GitLab, Bitbucket, or Azure DevOps repo attached. The schedule reuses that repo
            on every fire.
          </Typography>
        </li>
        <li>
          <Typography
            fontFamily='geist'
            fontSize='bodyMd'
            className='select-none text-tokens-neutral-text-secondary'
          >
            <strong>Integration test schedules need test inputs</strong> — chat stores the selected
            repo, flow names, deployment URL, and any extra notes in the scheduled request.
          </Typography>
        </li>
        <li>
          <Typography
            fontFamily='geist'
            fontSize='bodyMd'
            className='select-none text-tokens-neutral-text-secondary'
          >
            <strong>Once, or every N minutes / hours / days</strong> — supported schedule options
            are a single future run, or a fixed interval (minimum 60 seconds). Cron-style schedules
            aren’t exposed in chat.
          </Typography>
        </li>
        <li>
          <Typography
            fontFamily='geist'
            fontSize='bodyMd'
            className='select-none text-tokens-neutral-text-secondary'
          >
            <strong>Self-contained instructions</strong> — every fire runs in a brand-new
            conversation with no access to the originating chat, so write the request as if it were
            a fresh one-off ask.
          </Typography>
        </li>
      </ul>

      <div className='flex flex-col gap-2'>
        <Typography
          fontFamily='geist'
          fontSize='bodySm'
          fontWeight='semibold'
          className='select-none text-tokens-neutral-text-tertiary'
        >
          Example:
        </Typography>
        <div className='flex items-start gap-3 rounded-[10px] border border-tokens-neutral-border-2 bg-tokens-neutral-bg-white px-3 py-2'>
          <Typography
            fontFamily='geist'
            fontSize='bodySm'
            className='flex-1 text-tokens-neutral-text-secondary'
          >
            “{SCHEDULE_EXAMPLE_PROMPT}”
          </Typography>
          <button
            type='button'
            onClick={() => {
              void navigator.clipboard.writeText(SCHEDULE_EXAMPLE_PROMPT).then(
                () => BetterToast.success('Example copied'),
                () => BetterToast.error('Failed to copy example')
              )
            }}
            className='inline-flex h-7 shrink-0 items-center gap-1.5 rounded-md px-2 text-xs font-medium text-tokens-neutral-text-secondary transition-colors hover:bg-tokens-neutral-fill-fill-6 hover:text-tokens-neutral-text-primary select-none'
            aria-label='Copy scheduled task example'
          >
            <Copy className='h-3.5 w-3.5' aria-hidden='true' />
            Copy
          </button>
        </div>
      </div>
    </section>
  )
}
